import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";

const TagPill = ({ label, variant }) => {
  const styles =
    variant === "gold"
      ? { backgroundColor: "#C89A4E", color: "#14231B" }
      : { backgroundColor: "rgba(20,35,27,0.82)", color: "#EDE9E0" };
  return (
    <span
      className="label-caps rounded-full px-3 py-1 text-[10px] backdrop-blur-sm"
      style={styles}
    >
      {label}
    </span>
  );
};

const ResidenceCard = ({ home, featured = false }) => {
  return (
    <Link
      to={`/residences/${home.slug}`}
      data-testid={`residence-card-${home.slug}`}
      className="group block transition-transform duration-500 ease-out hover:-translate-y-1"
    >
      <figure className="relative overflow-hidden" style={{ borderRadius: 24 }}>
        <div className="absolute left-4 top-4 z-10 flex flex-wrap gap-2">
          {home.tags.map((t) => (
            <TagPill key={t.label} label={t.label} variant={t.variant} />
          ))}
        </div>
        <img
          src={home.image}
          alt={home.alt}
          loading="lazy"
          className="w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          style={{ height: featured ? "clamp(420px, 56vw, 640px)" : 260 }}
        />
      </figure>

      <div className="mt-5 flex items-end justify-between gap-4">
        <div>
          <h3
            className="font-display"
            style={{ fontSize: featured ? "clamp(30px,3.4vw,44px)" : "clamp(24px,3vw,32px)", color: "#24382E", lineHeight: 1.05 }}
          >
            {home.name}
          </h3>
          <p className="mt-1 text-[15px]" style={{ color: "rgba(32,30,26,0.66)" }}>
            {home.location}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t pt-3" style={{ borderColor: "rgba(32,30,26,0.14)" }}>
        <span className="label-caps text-[13px]" style={{ color: "#24382E" }}>
          {home.price}
        </span>
        <span className="inline-flex items-center gap-2 text-[13px]" style={{ color: "#24382E" }}>
          <span className="label-caps">Book a showing</span>
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const FEATURED = {
  slug: "the-crawford",
  name: "The Crawford",
  location: "Mill Woods · Edmonton",
  price: "$2,150 / month",
  tags: [
    { label: "Now leasing", variant: "gold" },
    { label: "3 bed · 2.5 bath", variant: "pine" },
  ],
  image:
    "https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/the-crawford/hero/01-exterior.jpg",
  alt: "The Crawford — brand-new modern 7-plex in Mill Woods at dusk",
};

const SMALL = [
  {
    slug: "the-bridgeland",
    name: "The Bridgeland",
    location: "Bridgeland · above the bakery on 1st",
    price: "$2,100 / month",
    tags: [{ label: "Two bedroom", variant: "pine" }],
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    alt: "A bright apartment interior with soft neutral tones",
  },
  {
    slug: "10611-144-street-grovenor",
    name: "10611 144 Street",
    location: "Grovenor · Edmonton",
    price: "From $1,225 / month",
    tags: [{ label: "1 – 3 bedroom", variant: "pine" }],
    image:
      "https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/10611-144-street-grovenor/photo-01.jpg",
    alt: "10611 144 Street — brand-new 7-plex in Grovenor",
  },
];

export const ResidencesSection = () => {
  return (
    <section
      className="relative"
      style={{ backgroundColor: "#EDE9E0" }}
      data-nav-theme="light"
      data-testid="home-residences"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-36">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <Eyebrow testId="residences-eyebrow">Residences</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                className="font-display mt-7 max-w-[15ch]"
                style={{ fontSize: "clamp(36px,5vw,72px)", color: "#201E1A", lineHeight: 1.02 }}
              >
                Homes worth coming home to.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="body-measure mt-6 text-[17px]" style={{ color: "rgba(32,30,26,0.72)" }}>
                We&rsquo;re proud of the properties we manage. Check out some of our best ones and maybe find your next home.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <Link
              to="/residences"
              data-testid="residences-view-all"
              className="pill text-[15px]"
              style={{ border: "1px solid #24382E", color: "#24382E" }}
            >
              View all residences
            </Link>
          </Reveal>
        </div>

        {/* Asymmetric layout: featured 60% + two stacked, offset lower */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-7">
            <Reveal>
              <ResidenceCard home={FEATURED} featured />
            </Reveal>
          </div>
          <div className="flex flex-col gap-10 md:col-span-5 md:mt-28">
            {SMALL.map((h, i) => (
              <Reveal key={h.slug} delay={0.08 + i * 0.08}>
                <ResidenceCard home={h} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResidencesSection;
